import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import type { DepositStatus, KeywordDrift, PlatformConfigPublic } from '@repo/shared-types';
import { adminFetchApi } from '../lib/api';

const KEY = ['admin', 'platform-config'] as const;

export function usePlatformConfig() {
  return useQuery({
    queryKey: [...KEY, 'config'],
    queryFn: () => adminFetchApi<PlatformConfigPublic>('/admin/platform-config'),
  });
}

/** Auto-runs on page load, like the BankHub reconcile tab: MISSING_PRIMARY and
 *  EMPTY cost money, and an operator would never think to press a button. */
export function useKeywordDrift() {
  return useQuery({
    queryKey: [...KEY, 'drift'],
    queryFn: () => adminFetchApi<KeywordDrift>('/admin/platform-config/drift'),
  });
}

export function useRotateKeyword() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (keyword: string) =>
      adminFetchApi('/admin/platform-config/keyword', { method: 'POST', body: JSON.stringify({ keyword }) }),
    onSuccess: () => {
      toast.success('Đã đổi từ khóa');
      qc.invalidateQueries({ queryKey: KEY });
    },
    onError: (e: Error) => toast.error(e.message || 'Đổi từ khóa thất bại'),
  });
}

/** Re-pushes the CURRENT whitelist unchanged — the recovery action for a drift
 *  alarm. `/keyword` refuses a keyword that's already current, so this is the
 *  only route that can re-sync SePay when it, not our own config, drifted. */
export function useResyncKeyword() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => adminFetchApi('/admin/platform-config/resync', { method: 'POST' }),
    onSuccess: () => {
      toast.success('Đã đẩy whitelist lên SePay, đang kiểm tra lại đồng bộ…');
      qc.invalidateQueries({ queryKey: KEY });
    },
    onError: (e: Error) => toast.error(e.message || 'Đẩy lại thất bại'),
  });
}

export function usePurgeKeyword() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (keyword: string) =>
      adminFetchApi('/admin/platform-config/keyword/purge', { method: 'POST', body: JSON.stringify({ keyword }) }),
    onSuccess: () => {
      toast.success('Đã xoá từ khóa');
      qc.invalidateQueries({ queryKey: KEY });
    },
    onError: (e: Error) => toast.error(e.message || 'Xoá từ khóa thất bại'),
  });
}

export function useMerchantLogo() {
  return useQuery({
    queryKey: [...KEY, 'logo'],
    queryFn: () => adminFetchApi<{ logoUrl: string | null }>('/admin/platform-config/logo'),
  });
}

export function useUploadMerchantLogo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (blob: Blob) => {
      const form = new FormData();
      form.append('logo', blob, 'logo.png');
      // No Content-Type header: fetch must set the multipart boundary itself.
      return adminFetchApi('/admin/platform-config/logo', { method: 'POST', body: form });
    },
    onSuccess: () => {
      toast.success('Đã tải logo lên SePay');
      qc.invalidateQueries({ queryKey: [...KEY, 'logo'] });
    },
    onError: (e: Error) => toast.error(e.message || 'Tải logo thất bại'),
  });
}

export function useDeposit() {
  return useQuery({
    queryKey: [...KEY, 'deposit'],
    queryFn: () => adminFetchApi<DepositStatus>('/admin/platform-config/deposit'),
  });
}

export function useSaveDeposit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { balanceVnd: number; balanceAsOf: string; perTxnCostVnd: number; lowThresholdVnd: number }) =>
      adminFetchApi('/admin/platform-config/deposit-config', { method: 'PUT', body: JSON.stringify(body) }),
    onSuccess: () => {
      toast.success('Đã cập nhật ký quỹ');
      qc.invalidateQueries({ queryKey: [...KEY, 'deposit'] });
    },
    onError: () => toast.error('Cập nhật thất bại'),
  });
}
